import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: IDENTITY_USER.
 *
 * Covers cross-provider "a person/principal with access" semantics shared by
 * Okta/AAD/IAM users, GitHub members, Rippling employees. PII surface is
 * intentionally minimal — name + email + status + lifecycle dates only.
 * Sensitive attributes (SSN, DOB, salary, MFA secrets) are not in the
 * canonical schema and won't be projected even if the provider returns them.
 */

export const IDENTITY_USER_SCHEMA_VERSION = 2;

export const IdentityUser = z.object({
  /** Provider-stable identifier surfaced to operators. */
  externalId: z.string(),
  /** Primary email if known. Treated as PII per data minimization. */
  email: z.string().email().nullable(),
  /** Display / full name if known. PII. */
  displayName: z.string().nullable(),

  /** Account lifecycle. */
  status: z.enum(['active', 'suspended', 'deactivated', 'invited', 'unknown']),

  /** When the account was created in the source system. */
  createdAt: z.string().datetime().nullable(),
  /** Last activity timestamp where the provider exposes it (logins, last seen). */
  lastActiveAt: z.string().datetime().nullable(),
  /** When the account was deactivated/offboarded, if applicable. */
  deactivatedAt: z.string().datetime().nullable(),

  /** True when the principal has elevated privileges (admin/owner/superuser). */
  isPrivileged: z.boolean(),
  /** True when MFA is enforced/enrolled at the provider level. */
  isMfaEnrolled: z.boolean(),

  /** True for service accounts / bots — sampling for SOC 2 employee populations excludes these. */
  isServiceAccount: z.boolean(),
  /** Workforce relationship, populated only from explicit provider signals. */
  affiliation: z.enum(['workforce', 'guest', 'external', 'unknown']),
});

export type IdentityUser = z.infer<typeof IdentityUser>;
